/* eslint-disable react-hooks/set-state-in-effect -- fetch-on-mount via useCallback (loadOffers) */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    FileText, Euro, Clock, CheckCircle, XCircle, AlertCircle,
    Search, Filter, ChevronRight, User, Calendar, Building2,
    PenLine, Eye, Download, MoreVertical, Loader2, RefreshCw, History
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import OfferHistoryModal from '../../components/offers/OfferHistoryModal';
import AgencyMandatesForOffer from '../../components/offers/AgencyMandatesForOffer';
import CreateOfferModal from '../../components/offers/CreateOfferModal';

const statusConfig = {
    draft: { 
        label: 'Brouillon', 
        color: 'bg-gray-500', 
        icon: FileText,
        textColor: 'text-muted-foreground'
    },
    draft_buyer_submitted: { 
        label: 'Brouillon à valider',
        color: 'bg-amber-500',
        icon: AlertCircle,
        textColor: 'text-amber-400',
    },
    pending_buyer_signature: { 
        label: 'En attente signature', 
        color: 'bg-yellow-500', 
        icon: PenLine,
        textColor: 'text-yellow-400'
    },
    pending_seller_review: { 
        label: 'En attente vendeur', 
        color: 'bg-blue-500', 
        icon: Clock,
        textColor: 'text-blue-400'
    },
    accepted: { 
        label: 'Acceptée', 
        color: 'bg-green-500', 
        icon: CheckCircle,
        textColor: 'text-green-400'
    },
    rejected: { 
        label: 'Refusée', 
        color: 'bg-red-500', 
        icon: XCircle,
        textColor: 'text-red-400'
    },
    cancelled: { 
        label: 'Annulée', 
        color: 'bg-gray-600', 
        icon: XCircle,
        textColor: 'text-muted-foreground'
    },
    expired: { 
        label: 'Expirée', 
        color: 'bg-orange-500', 
        icon: AlertCircle,
        textColor: 'text-orange-400'
    }
};

export default function OffersManagement({ isAdmin = false }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [offers, setOffers] = useState([]);
    const [stats, setStats] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedOfferForHistory, setSelectedOfferForHistory] = useState(null);
    const [activeTab, setActiveTab] = useState('offers'); // 'offers' | 'agency-mandates'
    const [createModalOpen, setCreateModalOpen] = useState(false);
    
    const actualIsAdmin = isAdmin || user?.role === 'admin' || user?.role === 'super_admin';

    const loadOffers = useCallback(async () => {
        setLoading(true);
        try {
            const endpoint = actualIsAdmin ? '/offers/admin/all' : '/offers/my';
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            
            const response = await api.get(`${endpoint}?${params.toString()}`);
            setOffers(response.data.offers || []);
            setStats(response.data.stats || null);
        } catch (err) {
            console.error('Error loading offers:', err);
            toast.error('Erreur lors du chargement des offres');
        } finally {
            setLoading(false);
        }
    }, [actualIsAdmin, statusFilter]);

    useEffect(() => {
        loadOffers();
    }, [loadOffers]);

    const openHistoryModal = (offer) => {
        setSelectedOfferForHistory(offer);
        setHistoryModalOpen(true);
    };

    const handleStatusChange = async (offerId, newStatus) => {
        try {
            await api.patch(`/offers/${offerId}/status`, { status: newStatus });
            toast.success('Statut mis à jour');
            loadOffers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const filteredOffers = offers.filter(offer => {
        if (!searchQuery) return true;
        const search = searchQuery.toLowerCase();
        return (
            offer.buyer_name?.toLowerCase().includes(search) ||
            offer.mandate?.mandate_number?.toLowerCase().includes(search) ||
            offer.mandate?.property?.address?.toLowerCase().includes(search) ||
            offer.agent?.name?.toLowerCase().includes(search)
        );
    });

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatAmount = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#FF914D] animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0f] p-3 sm:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-4 sm:mb-8 gap-4">
                    <div className="min-w-0">
                        <h1 className="text-xl sm:text-3xl font-bold text-foreground truncate">
                            {actualIsAdmin ? 'Gestion des Offres' : 'Mes Offres'}
                        </h1>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                            {actualIsAdmin 
                                ? 'Consultez et gérez vos offres d\'achat'
                                : 'Consultez et gérez vos offres d\'achat'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={() => setCreateModalOpen(true)}
                            className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold text-xs sm:text-sm h-9 px-3 sm:px-4 shadow-lg"
                            data-testid="create-offer-btn">
                            <Euro className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Nouvelle offre</span>
                        </Button>
                        <Button onClick={loadOffers} variant="outline"
                            className="border-border text-muted-foreground hover:text-foreground shrink-0 text-xs sm:text-sm h-9">
                            <RefreshCw className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Actualiser</span>
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-4 sm:mb-6">
                    <Button onClick={() => setActiveTab('offers')} size="sm"
                        className={`text-xs rounded-full h-7 px-3 sm:h-8 sm:px-4 ${activeTab === 'offers' 
                            ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg' 
                            : 'bg-card text-muted-foreground hover:text-white border border-border'}`}
                        data-testid="tab-offers">
                        <Euro className="w-3.5 h-3.5 mr-1.5" /> Offres
                    </Button>
                    <Button onClick={() => setActiveTab('agency-mandates')} size="sm"
                        className={`text-xs rounded-full h-7 px-3 sm:h-8 sm:px-4 ${activeTab === 'agency-mandates' 
                            ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg' 
                            : 'bg-card text-muted-foreground hover:text-white border border-border'}`}
                        data-testid="tab-agency-mandates">
                        <Building2 className="w-3.5 h-3.5 mr-1.5" /> Mandats agence
                    </Button>
                </div>

                {activeTab === 'agency-mandates' ? (
                    <AgencyMandatesForOffer />
                ) : (
                <>

                {/* Stats Cards — only show if there are offers */}
                {stats && stats.total > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-4 mb-4 sm:mb-8">
                        {[
                            { key: '', value: stats.total, label: 'Total', color: 'text-foreground', ring: 'ring-[#FF914D]' },
                            { key: 'pending_buyer_signature', value: stats.pending_signature, label: 'À signer', color: 'text-yellow-400', ring: 'ring-yellow-500' },
                            { key: 'pending_seller_review', value: stats.pending_review, label: 'En attente', color: 'text-blue-400', ring: 'ring-blue-500' },
                            { key: 'accepted', value: stats.accepted, label: 'Acceptées', color: 'text-green-400', ring: 'ring-green-500' },
                            { key: 'rejected', value: stats.rejected, label: 'Refusées', color: 'text-red-400', ring: 'ring-red-500' },
                        ].map(item => (
                            <Card key={item.label}
                                className={`bg-slate-900 border-border cursor-pointer transition-all hover:scale-[1.02] hover:border-white/20 ${
                                    statusFilter === item.key ? `ring-2 ${item.ring}` : ''
                                }`}
                                onClick={() => setStatusFilter(item.key)}
                                data-testid={`stat-card-${item.label.toLowerCase()}`}>
                                <CardContent className="p-3 sm:p-4 text-center">
                                    <p className={`text-xl sm:text-2xl font-bold ${item.color}`}>{item.value}</p>
                                    <p className="text-[10px] sm:text-xs text-muted-foreground">{item.label}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Empty state when no offers */}
                {stats && stats.total === 0 && (
                    <div className="text-center py-10 px-4" data-testid="offers-empty-state">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center mx-auto mb-4 shadow-xl">
                            <Euro className="w-7 h-7 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-1">Aucune offre pour le moment</h3>
                        <p className="text-sm text-muted-foreground mb-5 max-w-xs mx-auto">
                            Dès qu&apos;un acquéreur fera une offre sur l&apos;un de vos mandats, elle apparaîtra ici.
                        </p>
                        <Button onClick={() => setCreateModalOpen(true)}
                            className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold px-6 shadow-lg">
                            <Euro className="w-4 h-4 mr-2" /> Créer une offre manuellement
                        </Button>
                    </div>
                )}

                {/* Search and Filters */}
                <Card className="bg-slate-900 border-border mb-6">
                    <CardContent className="p-4">
                        <div className="flex flex-col gap-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Rechercher par acquéreur, mandat, adresse..."
                                    className="pl-10 bg-card border-border text-foreground"
                                />
                            </div>
                            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide" data-testid="offer-status-filter">
                                <button
                                    onClick={() => setStatusFilter('')}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap shrink-0 transition-all ${
                                        statusFilter === '' 
                                            ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg' 
                                            : 'bg-card border border-border text-muted-foreground hover:text-white'
                                    }`}
                                >
                                    Tous
                                </button>
                                {Object.entries(statusConfig).map(([key, config]) => (
                                    <button
                                        key={key}
                                        onClick={() => setStatusFilter(key)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap shrink-0 transition-all flex items-center gap-1 ${
                                            statusFilter === key 
                                                ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg' 
                                                : 'bg-card border border-border text-muted-foreground hover:text-white'
                                        }`}
                                    >
                                        <config.icon className="w-3 h-3" />
                                        {config.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Offers List */}
                <Card className="bg-slate-900 border-border">
                    <CardHeader>
                        <CardTitle className="text-foreground flex items-center gap-2">
                            <Euro className="w-5 h-5 text-[#FF914D]" />
                            Offres d&apos;achat ({filteredOffers.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {filteredOffers.length === 0 ? (
                            <div className="text-center py-12">
                                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">Aucune offre trouvée</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredOffers.map((offer, idx) => {
                                    const status = statusConfig[offer.status] || statusConfig.draft;
                                    const StatusIcon = status.icon;
                                    
                                    return (
                                        <motion.div
                                            key={offer.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.03 }}
                                            className="p-4 rounded-xl bg-card hover:bg-muted transition-colors"
                                        >
                                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                                {/* Main Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <Badge className={`${status.color} text-white text-xs`}>
                                                            <StatusIcon className="w-3 h-3 mr-1" />
                                                            {status.label}
                                                        </Badge>
                                                        <span className="text-xs text-muted-foreground">
                                                            {offer.offer_number || offer.id?.slice(0, 8)}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <User className="w-4 h-4 text-muted-foreground" />
                                                        <span className="font-medium text-foreground">
                                                            {offer.buyer_name || 'Acquéreur non défini'}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                        <Building2 className="w-4 h-4" />
                                                        <span className="truncate">
                                                            {typeof offer.mandate?.property?.address === 'object' 
                                                                ? `${offer.mandate.property.address.street || ''}, ${offer.mandate.property.address.city || ''}`
                                                                : offer.mandate?.property?.address || 
                                                                  offer.mandate?.mandate_number || 
                                                                  'Bien non spécifié'}
                                                        </span>
                                                    </div>
                                                    
                                                    {actualIsAdmin && offer.agent && (
                                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                                            <span>Agent: {offer.agent.name}</span>
                                                            {offer.is_inter_agent && (
                                                                <Badge className="bg-[#681793] text-white text-[10px]">Inter-agent</Badge>
                                                            )}
                                                        </div>
                                                    )}
                                                    {offer.is_inter_agent && offer.agent_approval_status === 'pending' && (
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px] border border-yellow-500/30">
                                                                <Clock className="w-3 h-3 mr-1" />
                                                                En attente d&apos;approbation
                                                            </Badge>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Amount & Date */}
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 lg:gap-6 mt-2 lg:mt-0">
                                                    <div className="text-left lg:text-right">
                                                        <p className="text-lg sm:text-xl font-bold text-foreground">
                                                            {formatAmount(offer.offer_amount)}
                                                        </p>
                                                        <p className="text-[11px] text-muted-foreground">
                                                            {offer.financing?.type === 'cash' ? 'Comptant' : 
                                                             offer.financing?.type === 'credit' ? 'Crédit' : 'Mixte'}
                                                        </p>
                                                    </div>
                                                    
                                                    <div className="text-left lg:text-right">
                                                        <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1">
                                                            <Calendar className="w-3.5 h-3.5" />
                                                            {formatDate(offer.created_at)}
                                                        </p>
                                                        {offer.validity_date && (
                                                            <p className="text-[10px] text-muted-foreground">
                                                                Jusqu&apos;au {formatDate(offer.validity_date)}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Actions — wrap clean on mobile (no overflow) */}
                                                    <div className="flex items-center gap-1 flex-wrap ml-auto lg:ml-0">
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            className="text-muted-foreground hover:text-foreground h-9 w-9"
                                                            onClick={() => openHistoryModal(offer)}
                                                            title="Historique"
                                                            data-testid={`offer-history-${offer.id}`}
                                                        >
                                                            <History className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            className="text-muted-foreground hover:text-foreground h-9 w-9"
                                                            onClick={() => window.open(`/offer/sign?token=${offer.signing_token}`, '_blank')}
                                                            title="Voir l&apos;offre"
                                                            data-testid={`offer-view-${offer.id}`}
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            className="text-muted-foreground hover:text-foreground h-9 w-9"
                                                            onClick={() => window.open(`/api/offers/${offer.id}/pdf`, '_blank')}
                                                            title="Télécharger le PDF"
                                                            data-testid={`offer-pdf-btn-${offer.id}`}
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </Button>
                                                        
                                                        {actualIsAdmin && (
                                                            <div className="relative group">
                                                                <Button
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="text-muted-foreground hover:text-foreground h-9 w-9"
                                                                    title="Plus d&apos;actions"
                                                                >
                                                                    <MoreVertical className="w-4 h-4" />
                                                                </Button>
                                                                <div className="absolute right-0 top-full mt-1 w-48 bg-slate-800 border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                                                    <div className="p-1">
                                                                        {offer.status === 'pending_seller_review' && (
                                                                            <>
                                                                                <button
                                                                                    onClick={() => handleStatusChange(offer.id, 'accepted')}
                                                                                    className="w-full text-left px-3 py-2 text-sm text-green-400 hover:bg-card rounded"
                                                                                >
                                                                                    <CheckCircle className="w-4 h-4 inline mr-2" />
                                                                                    Marquer acceptée
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => handleStatusChange(offer.id, 'rejected')}
                                                                                    className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-card rounded"
                                                                                >
                                                                                    <XCircle className="w-4 h-4 inline mr-2" />
                                                                                    Marquer refusée
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                        <button
                                                                            onClick={() => handleStatusChange(offer.id, 'cancelled')}
                                                                            className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-card rounded"
                                                                        >
                                                                            <XCircle className="w-4 h-4 inline mr-2" />
                                                                            Annuler l&apos;offre
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* History Modal */}
                <OfferHistoryModal
                    isOpen={historyModalOpen}
                    onClose={() => {
                        setHistoryModalOpen(false);
                        setSelectedOfferForHistory(null);
                    }}
                    offerId={selectedOfferForHistory?.id}
                    offerNumber={selectedOfferForHistory?.offer_number}
                />

                {/* Create Offer Modal */}
                <CreateOfferModal
                    isOpen={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                    onSuccess={loadOffers}
                />
                </>
                )}
            </div>
        </div>
    );
}
